import type { PaymentMethod } from './interfaces';
import type { MedicalCostType } from './types';
import type { GenericArray } from '@Types';
import { INTAKE_TYPES } from '@IntakeModule';
import {
  CART_SUMMARY_CATEGORY_KEYS,
  CART_TYPES,
  MEDICAL_COST_TYPES,
  OUTCOMES,
  OUTCOMES_TYPES,
  PAYMENT_METHODS,
} from './enums';

const ACTIVE_CART_ROUTES = [
  'animal-outcome',
  'animal-outcome-with-id',
  'animal-intake-payment',
  'animal-intake-with-id',
  'donation-intake-payment',
  'donation-with-id',
  'foster-payment',
  'foster-payment-with-id',
  'equipment-rental-payment',
  'equipment-rental-payment-with-id',
  'retail-payment',
  'citation-payment',
] as const;

// TODO :: TASK-2103 - This can be removed after all clients migrate to the new cart
const CART_ROUTE_NAMES = [
  // Active cart routes
  'animal-outcome',
  'animal-outcome-with-id',
  'animal-intake-payment',
  'animal-intake-with-id',
  'donation-intake-payment',
  'donation-with-id',
  // Pending payment routes
  'animal-outcome-pending',
  'animal-intake-payment-pending',
  'donation-payment-pending',
  // Completed cart routes
  'animal-outcome-completed',
  'animal-intake-payment-completed',
  'donation-payment-completed',
  'foster-payment-pending',
  'foster-payment-completed',
  'equipment-rental-payment-pending',
  'equipment-rental-payment-completed',
  'retail-payment',
  'retail-payment-pending',
  'retail-payment-completed',
  'citation-payment',
  'citation-payment-pending',
  'citation-payment-completed',
] as const;

// NOTE :: Mirrors $live_receipt_cart_types in the api PrintFilesController::printAllCartDocuments().
// Only these cart types have a receipt that differs from the full document packet, so only they get
// a Receipt / All Documents choice. For every other type print-all already returns just the receipt
// and ?receipt_only=1 is a no-op, so the UI must show a single print button instead of two identical
// options. Typed as string[] so callers can test a raw cart_type without casting.
const CART_TYPES_WITH_RECEIPT_ONLY_PRINT: string[] = [
  CART_TYPES.INTAKE,
  CART_TYPES.OUTCOME,
  OUTCOMES.LOST_AND_FOUND,
  OUTCOMES.REHOME,
];

const CHECK_IN_INTAKE_TYPES = [INTAKE_TYPES.BOARDING_IN, INTAKE_TYPES.CLINIC_IN, INTAKE_TYPES.SERVICE_IN];

const EDITABLE_CART_SUMMARY_CATEGORY_KEYS: CART_SUMMARY_CATEGORY_KEYS[] = [
  CART_SUMMARY_CATEGORY_KEYS.ADDITIONAL_FEES,
  CART_SUMMARY_CATEGORY_KEYS.CITATIONS,
  CART_SUMMARY_CATEGORY_KEYS.DONATIONS,
  CART_SUMMARY_CATEGORY_KEYS.MEDICAL_COSTS,
  CART_SUMMARY_CATEGORY_KEYS.RETAIL,
];

const CITATION_ROUTES = {
  active: '/shelter/citation/payment/',
  pending: '/shelter/citation/payment-pending/',
  completed: '/shelter/citation/payment-completed/',
};

const DONATION_ROUTES = {
  active: '/shelter/donation/payment/',
  completed: '/shelter/donation/payment-completed/',
  pending: '/shelter/donation/payment-pending/',
};

const FOSTER_ROUTES = {
  active: '/shelter/animal/foster-payment/',
  pending: '/shelter/animal/foster-payment-pending/',
  completed: '/shelter/animal/foster-payment-completed/',
};

const EQUIPMENT_RENTAL_ROUTES = {
  active: '/shelter/animal/equipment-rental-payment/',
  pending: '/shelter/animal/equipment-rental-payment-pending/',
  completed: '/shelter/animal/equipment-rental-payment-completed/',
};

const INTAKE_ROUTES = {
  active: '/shelter/animal/intake-payment/',
  completed: '/shelter/animal/intake-payment-completed/',
  pending: '/shelter/animal/intake-payment-pending/',
};

const INTAKES_WITH_PAYMENT = [
  INTAKE_TYPES.BOARDING_IN,
  INTAKE_TYPES.CLINIC_IN,
  INTAKE_TYPES.DOA,
  INTAKE_TYPES.OWNER_REQUESTED_EUTHANASIA,
  INTAKE_TYPES.OWNER_SURRENDER,
  INTAKE_TYPES.RETURN,
  INTAKE_TYPES.SEIZED,
  INTAKE_TYPES.SERVICE_IN,
  INTAKE_TYPES.STRAY,
  INTAKE_TYPES.TNVR,
  INTAKE_TYPES.TRANSFER_IN,
  INTAKE_TYPES.WILDLIFE_IN,
];

const OUTCOME_ROUTES = {
  active: '/shelter/animal/outcome/',
  completed: '/shelter/animal/outcome-completed/',
  pending: '/shelter/animal/outcome-pending/',
};

const RETAIL_ROUTES = {
  active: '/shelter/retail/payment/',
  pending: '/shelter/retail/payment-pending/',
  completed: '/shelter/retail/payment-completed/',
};

// NOTE :: One place that maps a cart type to its three views. The store used to switch on cart_type
// in five separate places, so a new type meant five edits and any missed one silently fell through
// to the outcome routes.
//
// Not every cart_type is listed on purpose. `boarding` (in API ShoppingCartType) and the legacy
// `lost_found` / `rehome` values still in the data have no views of their own — they fall back to
// OUTCOME_ROUTES in buildCartRoute, which is where they have always gone. Add an entry here only
// when a type gets its own screens.
const CART_TYPE_ROUTES: Record<string, typeof OUTCOME_ROUTES> = {
  [CART_TYPES.CITATION]: CITATION_ROUTES,
  [CART_TYPES.DONATION]: DONATION_ROUTES,
  [CART_TYPES.EQUIPMENT_RENTAL]: EQUIPMENT_RENTAL_ROUTES,
  [CART_TYPES.FOSTER]: FOSTER_ROUTES,
  [CART_TYPES.INTAKE]: INTAKE_ROUTES,
  [CART_TYPES.OUTCOME]: OUTCOME_ROUTES,
  [CART_TYPES.PRODUCT]: RETAIL_ROUTES,
};

const OUTCOMES_WITH_CART = [
  OUTCOMES_TYPES.ADOPTED,
  OUTCOMES_TYPES.CLINIC_OUT,
  OUTCOMES_TYPES.RETURN_TO_OWNER,
  OUTCOMES_TYPES.SERVICE_OUT,
];

const OUTCOMES_WITH_CONTRACT = ['adopted', 'return_to_owner', 'transfer_out', 'service_out', 'clinic_out'];

// NOTE :: Outcomes that can issue vouchers. The api keeps the same list
// (OutcomeTypes::outcomeTypesWithVouchers); change both together. 'boarding_out' is a
// shelter-made outcome form matched by its key, so it has no OUTCOMES_TYPES member.
const OUTCOMES_WITH_VOUCHERS = ['adopted', 'return_to_owner', 'clinic_out', 'service_out', 'boarding_out'];

// Outcome type → the shelter profile switch a Super Admin uses to turn that contract off.
const OUTCOME_CONTRACT_FLAGS: Record<string, string> = {
  adopted: 'adoption_contract',
  return_to_owner: 'rto_contract',
  transfer_out: 'transfer_out_contract',
};

const PAYMENT_OPTIONS = <PaymentMethod[]>[
  {
    icon: 'faCreditCard',
    key: 'credit_card',
    label: 'cart.paymentOptions.creditCard',
    value: PAYMENT_METHODS.CREDIT,
  },
  {
    icon: 'faDollarSign',
    key: 'cash',
    label: 'cart.paymentOptions.cash',
    value: PAYMENT_METHODS.CASH,
  },
  {
    icon: 'faCheck',
    key: 'check',
    label: 'cart.paymentOptions.check',
    value: PAYMENT_METHODS.CHECK,
  },
  {
    icon: 'faSdCard',
    key: 'card_reader',
    label: 'cart.paymentOptions.cardReader',
    value: PAYMENT_METHODS.CARD_READER,
  },
  {
    icon: 'faCashRegister',
    key: 'other',
    label: 'cart.paymentOptions.other',
    value: PAYMENT_METHODS.OTHER,
  },
  {
    icon: 'faMoneyCheckDollar',
    key: 'split_payment',
    label: 'cart.paymentOptions.splitPayment',
    value: PAYMENT_METHODS.SPLIT_PAYMENT,
  },
];

const SANITIZED_INTAKE_TYPES: GenericArray = {
  [INTAKE_TYPES.ADD_PET]: 'cart.intakeOptions.add_pet',
  [INTAKE_TYPES.ADD_TRANSPORT_ANIMAL]: 'cart.intakeOptions.add_transport_animal',
  [INTAKE_TYPES.BOARDING_IN]: 'cart.intakeOptions.boarding_in',
  [INTAKE_TYPES.BORN_IN_CARE]: 'cart.intakeOptions.born_in_care',
  [INTAKE_TYPES.CLINIC_IN]: 'cart.intakeOptions.clinic_in',
  [INTAKE_TYPES.DOA]: 'cart.intakeOptions.doa',
  [INTAKE_TYPES.FOUND]: 'cart.intakeOptions.found',
  [INTAKE_TYPES.LOST]: 'cart.intakeOptions.lost',
  [INTAKE_TYPES.OWNER_REQUESTED_EUTHANASIA]: 'cart.intakeOptions.owner_requested_euthanasia',
  [INTAKE_TYPES.OWNER_SURRENDER]: 'cart.intakeOptions.owner_surrender',
  [INTAKE_TYPES.RETURN]: 'cart.intakeOptions.return',
  [INTAKE_TYPES.SEIZED]: 'cart.intakeOptions.seized',
  [INTAKE_TYPES.SERVICE_IN]: 'cart.intakeOptions.service_in',
  [INTAKE_TYPES.STRAY]: 'cart.intakeOptions.stray',
  [INTAKE_TYPES.TNVR]: 'cart.intakeOptions.tnvr',
  [INTAKE_TYPES.TRANSFER_IN]: 'cart.intakeOptions.transfer_in',
  [INTAKE_TYPES.UNKNOWN]: 'cart.intakeOptions.unknown',
  [INTAKE_TYPES.WILDLIFE_IN]: 'cart.intakeOptions.wildlife_in',
};

const SANITIZED_OUTCOME_TYPES: GenericArray = {
  adopted: 'cart.outcomeOptions.adopted',
  clinic_out: 'cart.outcomeOptions.clinic_out',
  died_in_care: 'cart.outcomeOptions.died_in_care',
  doa: 'cart.outcomeOptions.doa',
  finder_kept: 'cart.outcomeOptions.finderKept',
  lost: 'cart.outcomeOptions.lost',
  owner_requested_euthanasia: 'cart.outcomeOptions.owner_requested_euthanasia',
  rehome: 'cart.outcomeOptions.rehome',
  rehome_shelter_intake: 'cart.outcomeOptions.rehome_shelter_intake',
  rehome_unknown: 'cart.outcomeOptions.rehome_unknown',
  rehome_deceased: 'cart.outcomeOptions.rehome_deceased',
  rehomed: 'cart.outcomeOptions.rehomed',
  remained_with_finder: 'cart.outcomeOptions.remained_with_finder',
  remained_with_owner: 'cart.outcomeOptions.remained_with_owner',
  return_to_field: 'cart.outcomeOptions.return_to_field',
  wildlife_out: 'cart.outcomeOptions.wildlife_out',
  return_to_owner: 'cart.outcomeOptions.return_to_owner',
  reunited_with_owner: 'cart.outcomeOptions.reunited_with_owner',
  lost_found_returned_to_field: 'cart.outcomeOptions.lost_found_returned_to_field',
  lost_found_deceased: 'cart.outcomeOptions.lost_found_deceased',
  service_out: 'cart.outcomeOptions.service_out',
  shelter_euthanasia: 'cart.outcomeOptions.shelter_euthanasia',
  shelter_intake: 'cart.outcomeOptions.shelter_intake',
  transfer_out: 'cart.outcomeOptions.transfer_out',
  unknown: 'cart.outcomeOptions.unknown',
};

const TARGET_EDIT_FIELDS = [
  'paid_date',
  'payment_type',
  'last_four_digits',
  'check_number',
  'receipt_notes',
  'split_payment_details',
  'donations_details',
  'total_charge',
] as const;

const MEDICAL_COLLECTIONS: MedicalCostType[] = [
  MEDICAL_COST_TYPES.MEDICATIONS,
  MEDICAL_COST_TYPES.VACCINATIONS,
  MEDICAL_COST_TYPES.PROCEDURES,
  MEDICAL_COST_TYPES.DIAGNOSTICS,
  MEDICAL_COST_TYPES.SURGERIES,
  MEDICAL_COST_TYPES.EXAMS,
];

export {
  ACTIVE_CART_ROUTES,
  TARGET_EDIT_FIELDS,
  CART_ROUTE_NAMES,
  CART_TYPE_ROUTES,
  CART_TYPES_WITH_RECEIPT_ONLY_PRINT,
  CHECK_IN_INTAKE_TYPES,
  CITATION_ROUTES,
  EDITABLE_CART_SUMMARY_CATEGORY_KEYS,
  DONATION_ROUTES,
  EQUIPMENT_RENTAL_ROUTES,
  FOSTER_ROUTES,
  INTAKES_WITH_PAYMENT,
  INTAKE_ROUTES,
  MEDICAL_COLLECTIONS,
  OUTCOMES_WITH_CART,
  OUTCOMES_WITH_CONTRACT,
  OUTCOMES_WITH_VOUCHERS,
  OUTCOME_CONTRACT_FLAGS,
  OUTCOME_ROUTES,
  PAYMENT_OPTIONS,
  RETAIL_ROUTES,
  SANITIZED_INTAKE_TYPES,
  SANITIZED_OUTCOME_TYPES,
};
